/**
 * ChartSummary — Visual summary strip above the item table.
 * Shows item distributions as interactive mini charts.
 * Clicking a segment filters the table to that value.
 * Chart type is selectable via tab bar.
 */

import { useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie
} from 'recharts';
import {
  TrendingUp, Layers, CheckCircle2, AlertTriangle,
  PieChart as PieIcon, Shield, GitBranch,
} from 'lucide-react';
import { resolveCategoryHex, normalizeCategory } from '../../../../lib/itemCategoryColors';
import { useTheme } from '../../../../contexts/ThemeContext';
import { coerceChartType, type ChartType } from './chartTypes';

// ============================================================================
// Types
// ============================================================================

interface ChartSummaryProps {
  rows: Array<{
    id: string;
    itemCategory?: string;
    detailedState?: string;
    altitudeLevel?: number;
    hasBaseCompleteness: boolean;
    missingBaseFields: number;
    createdAt: Date | string;
    lifecycle_stage?: string;
    isLeadershipApproved?: boolean;
    inItemPool?: boolean;
    hasCatchballProposal?: boolean;
    catchballStatus?: string;
  }>;
  totalCount: number;
  filteredCount: number;
  onFilterByField: (field: string, value: string) => void;
  activeChart?: ChartType;
  onChartTypeChange?: (type: ChartType) => void;
}

// ============================================================================
// Color palettes
// ============================================================================

// Resolved per render rather than bound at module load, so the palette follows
// whichever light ramp is active. resolveCategoryHex falls back to the classic
// hex when no --cat-* variable is published.

// ============================================================================
// Chart tab definitions
// ============================================================================

const CHART_TABS: { id: ChartType; label: string; icon: React.ReactNode }[] = [
  { id: 'category',     label: 'Category',    icon: <PieIcon className="h-3.5 w-3.5" /> },
  { id: 'altitude',     label: 'Altitude',    icon: <Layers className="h-3.5 w-3.5" /> },
  { id: 'lineage',      label: 'Lineage',     icon: <GitBranch className="h-3.5 w-3.5" /> },
  { id: 'completeness', label: 'Completeness', icon: <Shield className="h-3.5 w-3.5" /> },
];

// ============================================================================
// Tooltip
// ============================================================================

type ChartTooltipPayload = {
  payload: { label?: string; name?: string; count: number };
};

function CustomTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: ChartTooltipPayload[];
}) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const dark = document.documentElement.classList.contains('dark');
  return (
    <div style={dark ? {
      background: 'var(--surface)', border: '1px solid var(--rule-bright)',
      borderRadius: '3px', padding: '6px 10px', fontSize: '11px',
      color: 'var(--text-pri)', boxShadow: 'none',
    } : {
      background: '#fff', border: '1px solid #e5e7eb',
      borderRadius: '8px', padding: '6px 12px', fontSize: '12px',
      color: '#111827', boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
    }}>
      <div style={{ fontWeight: 600 }}>{d.label ?? d.name}</div>
      <div style={{ opacity: 0.65, marginTop: 2 }}>{d.count} item{d.count !== 1 ? 's' : ''}</div>
    </div>
  );
}

// ============================================================================
// Chart renderers
// ============================================================================

function CategoryChart({
  data,
  total,
  onFilter,
}: {
  data: { name: string; label: string; count: number; color: string }[];
  total: number;
  onFilter: (field: string, value: string) => void;
}) {
  const [hovered, setHovered] = useState<string | null>(null);
  return (
    <div className="space-y-1.5">
      {data.map((cat) => {
        const pct = total > 0 ? (cat.count / total) * 100 : 0;
        return (
          <button
            key={cat.name}
            onClick={() => onFilter('itemCategory', cat.name === 'unset' ? '' : cat.name)}
            onMouseEnter={() => setHovered(cat.name)}
            onMouseLeave={() => setHovered(null)}
            className={`w-full flex items-center gap-2 group transition-all duration-150  px-1.5 py-0.5 -mx-1.5 ${
              hovered === cat.name ? 'bg-[var(--hover-tint)]' : ''
            }`}
          >
            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
            <span className="text-xs text-[var(--text-sec)] font-medium truncate">{cat.label}</span>
            <div className="flex-1 h-1.5 bg-[var(--hover-tint)] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{ width: `${pct}%`, backgroundColor: cat.color, opacity: hovered && hovered !== cat.name ? 0.3 : 0.7 }}
              />
            </div>
            <span className="text-[11px] text-[var(--text-ter)] tabular-nums font-medium w-6 text-right">{cat.count}</span>
          </button>
        );
      })}
    </div>
  );
}

function AltitudeChart({
  data,
  onFilter,
  isDark,
}: {
  data: { name: string; label: string; count: number; color: string }[];
  onFilter: (field: string, value: string) => void;
  isDark: boolean;
}) {
  const [hovered, setHovered] = useState<string | null>(null);
  const axisColor = isDark ? '#4a5568' : '#9ca3af';
  const cursorColor = isDark ? 'rgba(255,255,255,0.04)' : '#f3f4f6';
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} layout="vertical" barCategoryGap="15%">
        <XAxis type="number" hide />
        <YAxis type="category" dataKey="label" tick={{ fontSize: 10, fill: axisColor }} tickLine={false} axisLine={false} width={55} />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: cursorColor }} />
        <Bar
          dataKey="count"
          radius={[0, 3, 3, 0]}
          cursor="pointer"
          onClick={(d) => {
            const name = (d as { name?: string } | undefined)?.name;
            if (name) onFilter('altitudeLevel', name);
          }}
          onMouseEnter={(_d, idx) => setHovered(data[idx]?.name ?? null)}
          onMouseLeave={() => setHovered(null)}
        >
          {data.map((entry, i) => (
            <Cell key={`alt-${i}`} fill={entry.color} opacity={hovered && hovered !== entry.name ? 0.4 : 1} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function LineageChart({
  rows,
  onFilter,
}: {
  rows: ChartSummaryProps['rows'];
  onFilter: (field: string, value: string) => void;
}) {
  const total = rows.length;
  if (total === 0) return null;

  const approved = rows.filter((r) => r.isLeadershipApproved).length;
  const inPool = rows.filter((r) => r.inItemPool).length;
  const hasProposal = rows.filter((r) => r.hasCatchballProposal).length;
  const catchballCounts: Record<string, number> = {};
  for (const r of rows) {
    if (r.catchballStatus) {
      catchballCounts[r.catchballStatus] = (catchballCounts[r.catchballStatus] || 0) + 1;
    }
  }

  const segments = [
    { key: 'isLeadershipApproved', label: 'On Priority List', count: approved, color: '#34d399', field: 'isLeadershipApproved', value: 'true' },
    { key: 'inItemPool', label: 'In Item Pool', count: inPool, color: '#60a5fa', field: 'inItemPool', value: 'true' },
    { key: 'hasCatchballProposal', label: 'Has Catchball Proposal', count: hasProposal, color: '#a78bfa', field: 'hasCatchballProposal', value: 'true' },
  ];

  const statusColors: Record<string, string> = {
    pending: '#fbbf24', accepted: '#34d399', countered: '#f97316', rejected: '#f87171', withdrawn: '#9ca3af',
  };
  const statusLabels: Record<string, string> = {
    pending: 'Pending', accepted: 'Accepted', countered: 'Returned', rejected: 'Rejected', withdrawn: 'Withdrawn',
  };

  return (
    <div className="space-y-3">
      <div>
        <div className="text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-widest mb-2">Governance Status</div>
        <div className="space-y-1.5">
          {segments.map((seg) => {
            const pct = (seg.count / total) * 100;
            return (
              <button
                key={seg.key}
                onClick={() => onFilter(seg.field, seg.value)}
                className="w-full flex items-center gap-2 hover:bg-[var(--surface)] px-1 py-0.5 -mx-1 transition-colors"
              >
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: seg.color }} />
                <span className="text-xs text-[var(--text-sec)] w-36 text-left truncate">{seg.label}</span>
                <div className="flex-1 h-1.5 bg-[var(--hover-tint)] rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: seg.color, opacity: 0.7 }} />
                </div>
                <span className="text-[11px] text-[var(--text-ter)] tabular-nums w-6 text-right">{seg.count}</span>
              </button>
            );
          })}
        </div>
      </div>
      {Object.keys(catchballCounts).length > 0 && (
        <div>
          <div className="text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-widest mb-2">Catchball Outcomes</div>
          <div className="flex gap-2 flex-wrap">
            {Object.entries(catchballCounts).map(([status, count]) => (
              <button
                key={status}
                onClick={() => onFilter('catchballStatus', status)}
                className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium hover:opacity-80 transition-opacity"
                style={{ backgroundColor: (statusColors[status] ?? '#9ca3af') + '22', color: statusColors[status] ?? '#6b7280' }}
              >
                <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: statusColors[status] ?? '#9ca3af' }} />
                {statusLabels[status] ?? status}
                <span className="font-bold">{count}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CompletenessChart({
  rows,
}: {
  rows: ChartSummaryProps['rows'];
}) {
  const total = rows.length;
  const complete = rows.filter((r) => r.hasBaseCompleteness).length;
  const incomplete = total - complete;
  const pct = total > 0 ? Math.round((complete / total) * 100) : 0;

  const pieData = [
    { name: 'Complete', count: complete, color: '#34d399' },
    { name: 'Needs Work', count: incomplete, color: '#fbbf24' },
  ];

  // By-category breakdown
  const byCat: Record<string, { complete: number; total: number; color: string }> = {};
  for (const r of rows) {
    const cat = normalizeCategory(r.itemCategory) || 'unset';
    if (!byCat[cat]) byCat[cat] = { complete: 0, total: 0, color: resolveCategoryHex(cat) };
    byCat[cat].total++;
    if (r.hasBaseCompleteness) byCat[cat].complete++;
  }

  const catEntries = Object.entries(byCat)
    .sort((a, b) => b[1].total - a[1].total)
    .slice(0, 8);

  return (
    <div className="flex gap-8">
      {/* Donut */}
      <div className="flex-shrink-0 flex flex-col items-center justify-center w-32">
        <div className="relative">
          <PieChart width={96} height={96}>
            <Pie data={pieData} dataKey="count" cx={48} cy={48} innerRadius={28} outerRadius={44} startAngle={90} endAngle={-270} strokeWidth={0}>
              {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
            </Pie>
          </PieChart>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-lg font-bold text-[var(--text-pri)]">{pct}%</span>
          </div>
        </div>
        <div className="text-[10px] text-[var(--text-ter)] text-center mt-1">Base complete</div>
      </div>

      {/* By category */}
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-widest mb-2">By Category</div>
        <div className="space-y-1">
          {catEntries.map(([cat, stats]) => {
            const catPct = stats.total > 0 ? Math.round((stats.complete / stats.total) * 100) : 0;
            return (
              <div key={cat} className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: stats.color }} />
                <span className="text-xs text-[var(--text-sec)] w-24 truncate">
                  {cat === 'unset' ? 'Uncategorized' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                </span>
                <div className="flex-1 h-1.5 bg-[var(--hover-tint)] rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-300 bg-[var(--green)]" style={{ width: `${catPct}%` }} />
                </div>
                <span className="text-[10px] text-[var(--text-ter)] tabular-nums w-8 text-right">{stats.complete}/{stats.total}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function ChartSummary({
  rows, totalCount, filteredCount, onFilterByField, activeChart, onChartTypeChange,
}: ChartSummaryProps) {
  const [internalChart, setInternalChart] = useState<ChartType>('category');
  const chartType = coerceChartType(activeChart ?? internalChart);
  const handleChartChange = (t: ChartType) => {
    setInternalChart(t);
    onChartTypeChange?.(t);
  };
  const { isDark } = useTheme();

  const categoryData = useMemo(() => {
    const counts: Record<string, number> = {};
    // Group on the NORMALISED value. Keying on the raw one split `Problem`
    // from `problem` into two buckets that both render as "Problem" — one
    // coloured, one grey, because the colour maps are keyed lowercase.
    for (const r of rows) { const c = normalizeCategory(r.itemCategory) || 'unset'; counts[c] = (counts[c] || 0) + 1; }
    return Object.entries(counts)
      .map(([name, count]) => ({ name, label: name === 'unset' ? 'Uncategorized' : name.charAt(0).toUpperCase() + name.slice(1), count, color: resolveCategoryHex(name) }))
      .sort((a, b) => b.count - a.count);
  }, [rows]);

  const altitudeData = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const r of rows) { const a = String(r.altitudeLevel ?? 0); counts[a] = (counts[a] || 0) + 1; }
    const altColors = ['#818cf8','#a78bfa','#c084fc','#e879f9','#f472b6','#60a5fa'];
    return Object.entries(counts)
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([lvl, count], i) => ({ name: lvl, label: `L${lvl}`, count, color: altColors[i % altColors.length] }));
  }, [rows]);

  const stats = useMemo(() => {
    let ready = 0; let notReady = 0; let recentlyCreated = 0;
    const now = Date.now(); const sevenDays = 7 * 24 * 60 * 60 * 1000;
    for (const r of rows) {
      if (r.hasBaseCompleteness) ready++; else notReady++;
      const created = r.createdAt instanceof Date ? r.createdAt.getTime() : new Date(r.createdAt).getTime();
      if (now - created < sevenDays) recentlyCreated++;
    }
    return { ready, notReady, recentlyCreated };
  }, [rows]);

  if (rows.length === 0) return null;
  const isFiltered = filteredCount < totalCount;

  return (
    <div
      className="border-b border-[var(--rule)]"
      style={isDark
        ? { background: 'var(--bg-raised)' }
        : { background: 'linear-gradient(to bottom, rgba(249,250,251,0.8), #fff)' }
      }
    >
      <div className="px-6 pt-3 pb-4">
        {/* Stats row */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mb-3">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 bg-[var(--slate-dim)]">
              <Layers className="h-4 w-4 text-[var(--text-ter)]" />
            </div>
            <div>
              <div className="text-2xl font-bold text-[var(--text-pri)] leading-none [font-variant-numeric:tabular-nums]">{filteredCount}</div>
              <div className="text-[11px] text-[var(--text-ter)] uppercase tracking-wider font-medium mt-0.5">{isFiltered ? 'Matching' : 'Total'} Items</div>
            </div>
          </div>
          <div className="hidden sm:block w-px h-10 bg-[var(--hover-tint)]" />
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 bg-[var(--green-dim)]">
              <CheckCircle2 className="h-4 w-4 text-[var(--green)]" />
            </div>
            <div>
              <div className="text-2xl font-bold text-[var(--green)] dark:text-[var(--text-ter)] leading-none [font-variant-numeric:tabular-nums]">{stats.ready}</div>
              <div className="text-[11px] text-[var(--text-ter)] uppercase tracking-wider font-medium mt-0.5">Base complete</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 bg-[var(--mustard-dim)]">
              <AlertTriangle className="h-4 w-4 text-[var(--mustard)]" />
            </div>
            <div>
              <div className="text-2xl font-bold text-[var(--mustard)] leading-none [font-variant-numeric:tabular-nums]">{stats.notReady}</div>
              <div className="text-[11px] text-[var(--text-ter)] uppercase tracking-wider font-medium mt-0.5">Needs Work</div>
            </div>
          </div>
          <div className="hidden sm:block w-px h-10 bg-[var(--hover-tint)]" />
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 bg-[var(--slate-dim)]">
              <TrendingUp className="h-4 w-4 text-[var(--text-ter)]" />
            </div>
            <div>
              <div className="text-2xl font-bold text-[var(--text-ter)] leading-none [font-variant-numeric:tabular-nums]">{stats.recentlyCreated}</div>
              <div className="text-[11px] text-[var(--text-ter)] uppercase tracking-wider font-medium mt-0.5">New This Week</div>
            </div>
          </div>
          <div className="flex-1" />
          {/* Chart type selector */}
          <div
            className="flex items-center p-0.5 gap-0.5"
            style={{ background: isDark ? 'var(--surface)' : 'rgba(243,244,246,0.7)' }}
          >
            {CHART_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleChartChange(tab.id)}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium transition-all duration-150"
                style={chartType === tab.id
                  ? {
                      background: isDark ? 'var(--bg-raised)' : '#fff',
                      color: isDark ? 'var(--text-sec)' : '#4338ca',
                      boxShadow: isDark ? 'none' : '0 1px 2px rgba(0,0,0,0.08)',
                      border: isDark ? '1px solid var(--rule-bright)' : '1px solid transparent',
                    }
                  : {
                      background: 'transparent',
                      color: isDark ? 'var(--text-ter)' : '#6b7280',
                      border: '1px solid transparent',
                    }
                }
                title={tab.label}
              >
                {tab.icon}
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Chart area */}
        <div className="mt-1">
          {chartType === 'category' && (
            <CategoryChart data={categoryData} total={rows.length} onFilter={onFilterByField} />
          )}
          {chartType === 'altitude' && (
            <div className="h-[108px]">
              <AltitudeChart data={altitudeData} onFilter={onFilterByField} isDark={isDark} />
            </div>
          )}
          {chartType === 'lineage' && (
            <LineageChart rows={rows} onFilter={onFilterByField} />
          )}
          {chartType === 'completeness' && (
            <CompletenessChart rows={rows} />
          )}
        </div>
      </div>
    </div>
  );
}
